import { NAME_IN_USE, nameConflictMessage } from "@components/constants/orgObjectMessages";

/**
 * Helpers for OrganizationJobFamiliesTab (ER-11652).
 *
 * Builds list-query params for GET /job-family/, SearchBar chips (Draft/Active/Retired
 * plus non-default Archived), and pagination metadata. The object-specific "Used"
 * group (has_positions) was dropped on 2026-09-16.
 */

export const PAGE_SIZE = 25;

export const DEFAULT_STATUSES = { draft: true, active: true, retired: false };

export const DEFAULT_ARCHIVED = false;

const CHIP_LABEL_BY_VALUE = {
  draft: "Draft",
  active: "Active",
  retired: "Retired",
  show_archived: "Show Archived",
};

const STATUS_CHIP_ORDER = ["draft", "active", "retired"];

const CHIP_VALUE_BY_LABEL = Object.keys(CHIP_LABEL_BY_VALUE).reduce((acc, value) => {
  acc[CHIP_LABEL_BY_VALUE[value]] = value;
  return acc;
}, {});

export function selectedStatuses(statuses, quickStatus, archived) {
  if (quickStatus) {
    return archived && quickStatus !== "archived" ? [quickStatus, "archived"] : [quickStatus];
  }
  const source = statuses && typeof statuses === "object" ? statuses : DEFAULT_STATUSES;
  const selected = Object.keys(source).filter((key) => source[key]);
  if (archived && !selected.includes("archived")) {
    selected.push("archived");
  }
  return selected;
}

/**
 * Query params for GET /job-family/. Status is always sent when any checkbox
 * (or archived) is selected so the backend does not fall back to its draft+active default.
 */
export function buildListParams(filters, pagination) {
  const source = filters || {};
  const status = selectedStatuses(source.statuses, source.quickStatus, source.archived);
  const params = {
    status,
    search: typeof source.search === "string" ? source.search : "",
  };
  return {
    filters: params,
    pagination: {
      page: Number((pagination && pagination.page) || 1) || 1,
      pageSize: Number((pagination && pagination.pageSize) || PAGE_SIZE) || PAGE_SIZE,
    },
  };
}

export function chipsFromFilters(filters) {
  const source = filters || {};
  const chips = [];
  const statuses = source.statuses && typeof source.statuses === "object" ? source.statuses : {};
  STATUS_CHIP_ORDER.forEach((key) => {
    if (statuses[key] && CHIP_LABEL_BY_VALUE[key]) {
      chips.push({ value: key, label: CHIP_LABEL_BY_VALUE[key] });
    }
  });
  if (source.archived) {
    chips.push({ value: "show_archived", label: CHIP_LABEL_BY_VALUE.show_archived });
  }
  return chips;
}

/** Flat list of applied filter values (statuses, show_archived) for default-aware checks. */
export function appliedFilterValues(filters) {
  return chipsFromFilters(filters).map((chip) => chip.value);
}

export function chipLabels(filters) {
  return chipsFromFilters(filters).map((chip) => chip.label);
}

export function removeChipFromFilters(filters, chipLabel) {
  const next = {
    statuses: { ...DEFAULT_STATUSES, ...((filters && filters.statuses) || {}) },
    archived: Boolean(filters && filters.archived),
    search: (filters && filters.search) || "",
    quickStatus: (filters && filters.quickStatus) || "",
  };
  const value = CHIP_VALUE_BY_LABEL[chipLabel] || chipLabel;
  if (value === "draft" || value === "active" || value === "retired") {
    next.statuses = { ...next.statuses, [value]: false };
  } else if (value === "show_archived") {
    next.archived = false;
  }
  return next;
}

export function pageCountFromResponse(data, pageSize = PAGE_SIZE) {
  const count = Number(data && data.count);
  const size = Number(pageSize) || PAGE_SIZE;
  if (!Number.isFinite(count) || count <= 0) return 1;
  return Math.max(1, Math.ceil(count / size));
}

function firstValidationMessage(value) {
  if (Array.isArray(value)) return value[0] || "";
  if (typeof value === "string") return value;
  return "";
}

/**
 * Extract `name` field errors from an axios rejection (DRF field errors) for the
 * Add/Edit Job Family dialogs. Always returns the dialog's `{ name }` shape; a
 * JOB_FAMILY_DUPLICATE_NAME rejection (EC §9 — account-scoped, case-insensitive,
 * archived excluded) maps to a human-readable Name error. Unknown shapes yield `{}`.
 */
export function familyNameFieldErrors(error) {
  const data = error && error.response && error.response.data;
  if (!data || typeof data !== "object") return {};
  if (typeof data.name === "string") {
    return { name: data.name };
  }
  if (Array.isArray(data.name) && data.name.length) {
    return { name: String(data.name[0]) };
  }
  // DRF surfaces the service's ValidationError as `{ code, detail }`, where `code`
  // carries the stable machine code and `detail` the human message. Check `code`
  // first so a present `detail` message never shadows the duplicate-name code.
  const detail = data.code || data.detail;
  if (detail === "JOB_FAMILY_DUPLICATE_NAME") {
    return { name: NAME_IN_USE };
  }
  return {};
}

/**
 * Extract `name` / `descriptor` field errors from an axios rejection (DRF field
 * errors) for the Add/Edit Family Level dialogs. Always returns the dialog's
 * `{ name, descriptor }` shape; unknown shapes yield `{ name: "", descriptor: "" }`.
 */
export function levelFieldErrors(error) {
  const data = error && error.response && error.response.data;
  if (!data || typeof data !== "object") return { name: "", descriptor: "" };
  return {
    name: nameConflictMessage(error) || firstValidationMessage(data.name),
    descriptor: firstValidationMessage(data.descriptor),
  };
}

/**
 * Extract the JOB_FAMILY_LEVEL_* error code from a delete rejection. DRF surfaces
 * the service's ValidationError as `{ code, detail }` in the response body.
 */
export function levelDeleteErrorCode(error) {
  const data = error && error.response && error.response.data;
  if (!data || typeof data !== "object") return "";
  return typeof data.code === "string" ? data.code : "";
}
